import React from 'react';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { createTransform, Spaces } from '@ff-os/rendering-shared/coordinates';
import { Size } from '@ff-os/shared-utils/types/Size';
import { Translate } from '@ff-os/shared-utils/types/Translate';
import { createDOMFrameScheduler, Paintable, Pipeline } from 'ff-canvas';
import { applyPreviewClipToDesignArea } from './applyPreviewClipToDesignArea';
import { createPipeline } from './createPipeline';

export type Engine2d = {
    pipeline: Pipeline;
    requestRender: () => void;
    disposeByIds: (ids: string[]) => void;
    dispose: () => void;
    is: (size: Size, spaces: Spaces) => boolean;
};

export type Engine2dOptions = {
    previewClip?: Clip | undefined;
    onRender?: (imageUrl: string) => Promise<void>;
    useDevicePixelRatio?: boolean;
};

export const createEngine2d = (
    paintables: React.MutableRefObject<Paintable[]>,
    canvas: HTMLCanvasElement,
    pxSize: Size,
    spaces: Spaces,
    translation: { t: Translate; language: string },
    options?: Engine2dOptions
): Engine2d => {

    const { requestRender, cancelRender } = createDOMFrameScheduler(async () => {

        pipeline.clear(undefined);
        pipeline.paintAll(paintables.current!);

        if (options?.previewClip) {
            applyPreviewClipToDesignArea(canvas, options.previewClip, spaces, pipeline);
        }

        if (options?.onRender) {
            await options.onRender(canvas.toDataURL());
        }
    });

    canvas.width = pxSize.width;
    canvas.height = pxSize.height;

    canvas.style.width = pxSize.width + 'px';
    canvas.style.height = pxSize.height + 'px';

    const pipeline = createPipeline(canvas, translation, {
        requestRender,
        transform: createTransform(pxSize.width, pxSize.height, spaces),
        useDevicePixelRatio: options?.useDevicePixelRatio ?? true
    });

    const dispose = () => {
        cancelRender();
        pipeline.disposeAll();
    };

    const disposeByIds = (ids: string[]): void => {
        for (const id of ids) {
            pipeline.disposeById(id);
        }
    };

    return {

        pipeline,
        requestRender,
        disposeByIds,
        dispose: dispose,

        is: (newPxSize, newSpaces) => {

            const spacesKeys: (keyof Spaces)[] = [
                'sceneWidth', 'sceneHeight',
                'designAreaX', 'designAreaY',
                'designAreaWidth', 'designAreaHeight',
                'designWidth', 'designHeight'
            ];

            return spacesKeys.every(key => newSpaces[key] === spaces[key])
                && newPxSize.width === pxSize.width
                && newPxSize.height === pxSize.height;
        }
    };
};